import { Injectable } from '@angular/core';
import { ApplicationDatabase } from '../storage.service';

/**
 * Paths Service - Manages browser navigation history
 * 
 * Stores up to 30 visited URLs in chronological order
 * Uses simple numeric keys (1, 2, 3, etc.) for efficient querying
 * Always sorted by timestamp
 * 
 * KEY FEATURES:
 * - Max 30 URLs stored (oldest auto-removed when limit exceeded)
 * - Simple numeric primary key for fast lookups
 * - Timestamp-based sorting for accurate chronological order
 * - getLastPaths(count) returns last N URLs in reverse chronological order
 * - lastVisitedUrl always equals getLastPaths(1)[0]
 */
@Injectable({
  providedIn: 'root'
})
export class PathsService {
  private readonly MAX_PATHS = 30;
  
  // In-memory cache of last visited URL for quick access
  private cachedLastUrl: string | null = null;

  constructor(private db: ApplicationDatabase) {}

  /**
   * Add a URL to the history
   * Automatically manages max 30 entries and cleanup
   */
  async addPath(url: string): Promise<void> {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      console.warn('[PathsService] Invalid URL provided:', url);
      return;
    }

    // Don't track callback pages
    if (url.includes('nhs-callback') || url.includes('callback')) {
      return;
    }

    try {
      // If at max, remove oldest entry before adding new one
      const count = await this.db.paths.count();
      if (count >= this.MAX_PATHS) {
        const oldest = await this.db.paths
          .orderBy('timestamp')
          .first();
        
        if (oldest && oldest.id !== undefined) {
          await this.db.paths.delete(oldest.id);
        }
      }

      // Get the highest ID currently in use (or 0 if no entries)
      const lastEntry = await this.db.paths
        .orderBy('id')
        .last();
      
      const newId = lastEntry ? lastEntry.id + 1 : 1;
      const now = Date.now();
      
      await this.db.paths.add({
        id: newId,
        key: `${newId}`,
        url: url,
        timestamp: now
      });

      // Update cache
      this.cachedLastUrl = url;
      
      console.debug('[PathsService] Added path:', url, 'ID:', newId);
    } catch (error) {
      console.error('[PathsService] Error adding path:', error);
    }
  }

  /**
   * Get the last N visited URLs in reverse chronological order
   * getLastPaths(1) = ['/create-account']
   * getLastPaths(3) = ['/create-account', '/login', '/guest']
   * 
   * @param count - Number of URLs to return (default 1)
   * @returns Array of URLs, newest first
   */
  async getLastPaths(count: number = 1): Promise<string[]> {
    try {
      if (count < 1) return [];
      
      // Get entries sorted by timestamp descending (newest first)
      const entries = await this.db.paths
        .orderBy('timestamp')
        .reverse()
        .limit(count)
        .toArray();
      
      return entries.map(entry => entry.url);
    } catch (error) {
      console.error('[PathsService] Error getting last paths:', error);
      return [];
    }
  }

  /**
   * Get the last visited URL
   * Fastest method - uses in-memory cache when possible
   */
  async getLastVisitedUrl(): Promise<string> {
    // Try cache first for instant access
    if (this.cachedLastUrl) {
      return this.cachedLastUrl;
    }

    try {
      const [lastUrl] = await this.getLastPaths(1);
      if (lastUrl) {
        this.cachedLastUrl = lastUrl;
        return lastUrl;
      }
    } catch (error) {
      console.error('[PathsService] Error getting last visited URL:', error);
    }

    return '/guest';
  }

  /**
   * Get the second-to-last visited URL
   * Useful for "go back" functionality
   */
  async getSecondLastVisitedUrl(): Promise<string> {
    try {
      const [, secondUrl] = await this.getLastPaths(2);
      return secondUrl || '/guest';
    } catch (error) {
      console.error('[PathsService] Error getting second last visited URL:', error);
      return '/guest';
    }
  }

  /**
   * Check if a path was visited in the last N URLs
   */
  async wasPathVisited(path: string, lookback: number = 5): Promise<boolean> {
    try {
      const recentPaths = await this.getLastPaths(lookback);
      return recentPaths.some(url => url.includes(path));
    } catch (error) {
      console.error('[PathsService] Error checking visited path:', error);
      return false;
    }
  }

  /**
   * Clear all path history
   */
  async clearHistory(): Promise<void> {
    try {
      await this.db.paths.clear();
      this.cachedLastUrl = null;
      console.info('[PathsService] Path history cleared');
    } catch (error) {
      console.error('[PathsService] Error clearing history:', error);
    }
  }

  /**
   * Get total number of stored paths
   */
  async getPathCount(): Promise<number> {
    try {
      return await this.db.paths.count();
    } catch (error) {
      console.error('[PathsService] Error getting path count:', error);
      return 0;
    }
  }

  /**
   * Get all stored paths in chronological order (oldest first)
   */
  async getAllPaths(): Promise<string[]> {
    try {
      const entries = await this.db.paths
        .orderBy('timestamp')
        .toArray();
      
      return entries.map(entry => entry.url);
    } catch (error) {
      console.error('[PathsService] Error getting all paths:', error);
      return [];
    }
  }

  /**
   * Set cache when we know the last URL (optimization)
   */
  setLastUrlCache(url: string): void {
    this.cachedLastUrl = url;
  }
}
